import { useCallback } from 'react';
import { useChatState } from './useChatState';
import { useResponseHandlers } from './useResponseHandlers';
import { aiJobCreationService } from '../../services/aiJobCreationService';

export const useAIJobCreationChat = (restaurantId, selectedLocationId = null) => {
  const chatState = useChatState();
  const responseHandlers = useResponseHandlers(chatState);
  
  const {
    messages,
    inputMessage,
    isLoading,
    extractedData,
    conversationHistory,
    showFormPreview,
    setInputMessage,
    setShowFormPreview,
    setIsLoading,
    addUserMessage,
    addAIMessage,
    initializeChat
  } = chatState;

  const {
    handleMCPError,
    handleSuccessResponse,
    handleErrorResponse,
    handleNetworkError
  } = responseHandlers;

  const sendMessage = useCallback(async () => {
    if (!inputMessage.trim() || isLoading) {
      return;
    }

    const currentInput = inputMessage;
    addUserMessage(currentInput);
    setInputMessage('');
    setIsLoading(true);

    try {
      const data = await aiJobCreationService.processMessage(
        currentInput,
        conversationHistory,
        restaurantId,
        selectedLocationId 
      );

      if (data.success) {
        if (data.data && data.data.isError) {
          handleMCPError(data.data, currentInput);
          return;
        }
        handleSuccessResponse(data, currentInput);
      } else {
        handleErrorResponse(data, currentInput);
      }
    } catch (error) {
      handleNetworkError(error, currentInput);
    } finally {
      setIsLoading(false);
    }
  }, [
    inputMessage,
    isLoading,
    conversationHistory,
    restaurantId,
    selectedLocationId,
    addUserMessage,
    setInputMessage,
    setIsLoading,
    handleMCPError,
    handleSuccessResponse,
    handleErrorResponse,
    handleNetworkError
  ]);

  const createJobFromAI = useCallback(async (selectedLocationId, onJobCreated) => {
    // Use location from extracted data if available, otherwise use selected location
    let locationId = selectedLocationId;
    
    // If extracted data has location info, try to find the matching location ID
    if (extractedData.location && !selectedLocationId) {
      // For now, we'll still require manual selection as a fallback
      // In the future, we could match the location string to the location ID
    }
    
    if (!locationId) {
      alert('Por favor selecciona una ubicación');
      return;
    }

    setIsLoading(true);
    try {
      const data = await aiJobCreationService.createJob(
        extractedData,
        restaurantId,
        locationId
      );

      if (data.success) {
        addAIMessage('¡Excelente! He creado tu oferta de trabajo exitosamente. ¿Te gustaría crear otra oferta o necesitas ayuda con algo más?');
        setShowFormPreview(false);
        
        if (onJobCreated) {
          onJobCreated('¡Trabajo creado con IA exitosamente!');
        }
      } else {
        alert('Error al crear el trabajo: ' + (data.error || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error creating job:', error);
      alert('Error al crear el trabajo');
    } finally {
      setIsLoading(false);
    }
  }, [extractedData, restaurantId, addAIMessage, setShowFormPreview, setIsLoading]);

  return {
    // State
    messages,
    inputMessage,
    isLoading,
    extractedData,
    showFormPreview,
    
    // Setters
    setInputMessage,
    setShowFormPreview,
    
    // Actions
    sendMessage,
    createJobFromAI,
    initializeChat
  };
};
